import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";

type CampaignType = {
  id: number;
  key: string;
  display_name: string;
  description: string;
  is_enabled: boolean;
  is_default: boolean;
};

export default function CampaignTypes() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [localValues, setLocalValues] = useState<Record<number, { display_name: string; description: string; is_enabled: boolean; is_default: boolean }>>({});

  // Query campaign types
  const { data: campaignTypes = [], isLoading } = useQuery<CampaignType[]>({
    queryKey: ["/api/admin/campaign-types"],
    queryFn: async () => {
      const response = await fetch("/api/admin/campaign-types", {
        credentials: 'include'
      });
      if (!response.ok) throw new Error("Failed to fetch campaign types");
      return response.json();
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: number; updates: { isEnabled?: boolean; displayName?: string; description?: string; isDefault?: boolean } }) => {
      const response = await fetch(`/api/admin/campaign-types/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: 'include',
        body: JSON.stringify(updates),
      });
      if (!response.ok) throw new Error("Failed to update campaign type");
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Saved" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/campaign-types"] });
    },
    onError: (error, { id }) => {
      toast({ title: "Failed", variant: "destructive" });

      // Revert to last good value
      const originalType = campaignTypes.find(t => t.id === id);
      if (originalType) {
        setLocalValues(prev => ({
          ...prev,
          [id]: {
            display_name: originalType.display_name,
            description: originalType.description,
            is_enabled: originalType.is_enabled,
            is_default: originalType.is_default,
          }
        }));
      }
    },
  });

  const handleToggle = (id: number, isEnabled: boolean) => {
    // Optimistically update local state
    setLocalValues(prev => ({
      ...prev,
      [id]: { ...getDisplayValues(id), is_enabled: isEnabled }
    }));

    updateMutation.mutate({ id, updates: { isEnabled } });
  };

  const handleDefaultToggle = (id: number, isDefault: boolean) => {
    // Optimistically update local state
    setLocalValues(prev => ({
      ...prev,
      [id]: { ...getDisplayValues(id), is_default: isDefault }
    }));

    updateMutation.mutate({ id, updates: { isDefault } });
  };

  const handleDisplayNameChange = (id: number, displayName: string) => {
    // Update local state immediately
    setLocalValues(prev => ({
      ...prev,
      [id]: { ...getDisplayValues(id), display_name: displayName }
    }));
  };

  const handleDisplayNameBlur = (id: number, displayName: string) => {
    const originalType = campaignTypes.find(t => t.id === id);
    if (originalType && originalType.display_name !== displayName) {
      updateMutation.mutate({ id, updates: { displayName } });
    }
  };

  const handleDescriptionChange = (id: number, description: string) => {
    // Update local state immediately
    setLocalValues(prev => ({
      ...prev,
      [id]: { ...getDisplayValues(id), description }
    }));
  };

  const handleDescriptionBlur = (id: number, description: string) => {
    const originalType = campaignTypes.find(t => t.id === id);
    if (originalType && originalType.description !== description) {
      updateMutation.mutate({ id, updates: { description } });
    }
  };

  const getDisplayValues = (id: number) => {
    const localValue = localValues[id];
    const originalType = campaignTypes.find(t => t.id === id);

    return {
      display_name: localValue?.display_name ?? originalType?.display_name ?? "",
      description: localValue?.description ?? originalType?.description ?? "",
      is_enabled: localValue?.is_enabled ?? originalType?.is_enabled ?? false,
      is_default: localValue?.is_default ?? originalType?.is_default ?? false,
    };
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Spinner />
      </div>
    );
  }

  return (
    <Card className="rounded-lg shadow-sm">
      <CardHeader>
        <CardTitle>Campaign Types</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {campaignTypes.map((type) => {
          const displayValues = getDisplayValues(type.id);

          return (
            <div key={type.id} className="p-4 border rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={displayValues.is_enabled}
                    onCheckedChange={(checked) => handleToggle(type.id, checked)}
                  />
                  <Label className="text-sm font-medium">
                    {displayValues.is_enabled ? "Enabled" : "Disabled"}
                  </Label>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={displayValues.is_default}
                    onCheckedChange={(checked) => handleDefaultToggle(type.id, checked)}
                    disabled={!displayValues.is_enabled}
                  />
                  <Label className="text-sm font-medium text-blue-600">
                    {displayValues.is_default ? "Default Category" : "Set as Default"}
                  </Label>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor={`name-${type.id}`} className="text-sm font-medium">
                  Name
                </Label>
                <Input
                  id={`name-${type.id}`}
                  value={displayValues.display_name}
                  onChange={(e) => handleDisplayNameChange(type.id, e.target.value)}
                  onBlur={(e) => handleDisplayNameBlur(type.id, e.target.value)}
                  disabled={!displayValues.is_enabled}
                  className="w-full"
                  placeholder="Campaign type name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor={`description-${type.id}`} className="text-sm font-medium">
                  Description
                </Label>
                <Textarea
                  id={`description-${type.id}`}
                  value={displayValues.description}
                  onChange={(e) => handleDescriptionChange(type.id, e.target.value)}
                  onBlur={(e) => handleDescriptionBlur(type.id, e.target.value)}
                  disabled={!displayValues.is_enabled}
                  className="w-full"
                  placeholder="Description of this campaign type"
                  rows={3}
                />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}